import type { DynamicEntitiesFetcher } from '../DynamicEntities/core/api/fetcher';

import { useState, useCallback, useRef } from 'react';
import { BulkDynamicEditService } from './services/BulkDynamicEditService';
import type { BulkProgressEvent } from './services/BulkDynamicEditService';
import type { EditorPhase, BulkProgressState, BulkUpdateResult } from './MasterCrud.types';

export interface UseBulkDynamicEditOptions {
  baseUrl: string;
  /** Transporte del anfitrión; ausente → `fetch` global. */
  fetcher?: DynamicEntitiesFetcher;
  featureCode: string;
  recordIds: string[];
  onComplete?: (result: BulkUpdateResult) => void;
}

export interface UseBulkDynamicEditReturn {
  phase: EditorPhase;
  progress: BulkProgressState;
  result: BulkUpdateResult | null;
  start: (payload: {
    entityPatches: Array<{ entityCode: string; fieldValues: Record<string, unknown> }>;
  }) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

const initialProgress: BulkProgressState = {
  processed: 0,
  total: 0,
  succeeded: 0,
  failed: 0,
  isComplete: false,
};

export function useBulkDynamicEdit({
  baseUrl,
  fetcher,
  featureCode,
  recordIds,
  onComplete,
}: UseBulkDynamicEditOptions): UseBulkDynamicEditReturn {
  const [phase, setPhase] = useState<EditorPhase>('editing');
  const [progress, setProgress] = useState<BulkProgressState>(initialProgress);
  const [result, setResult] = useState<BulkUpdateResult | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const start = useCallback(
    async (payload: {
      entityPatches: Array<{ entityCode: string; fieldValues: Record<string, unknown> }>;
    }) => {
      abortControllerRef.current = new AbortController();
      const service = new BulkDynamicEditService(baseUrl, fetcher);

      setPhase('processing');
      setProgress({ ...initialProgress, total: recordIds.length });
      setResult(null);

      try {
        await service.bulkUpdate(
          featureCode,
          { recordIds, entityPatches: payload.entityPatches },
          (event: BulkProgressEvent) => {
            setProgress({
              processed: event.processed,
              total: event.total,
              succeeded: event.succeeded,
              failed: event.failed,
              isComplete: event.isComplete,
            });

            if (event.isComplete) {
              const finalResult: BulkUpdateResult = {
                processed: event.processed,
                total: event.total,
                succeeded: event.succeeded,
                failed: event.failed,
                errors: event.errors ?? [],
              };
              setResult(finalResult);
              setPhase('completed');
              onComplete?.(finalResult);
            }
          },
          abortControllerRef.current.signal
        );
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          // Cancelación silenciosa
          setPhase('editing');
          setProgress(initialProgress);
          return;
        }
        throw error;
      }
    },
    [baseUrl, fetcher, featureCode, recordIds, onComplete]
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setPhase('editing');
    setProgress(initialProgress);
  }, []);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setPhase('editing');
    setProgress(initialProgress);
    setResult(null);
  }, []);

  return { phase, progress, result, start, cancel, reset };
}
